import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AppState, Platform, StyleSheet, View } from 'react-native';
import {
    AndroidAudioTypePresets,
    AudioSession,
    LiveKitRoom,
    useConnectionState,
    useLocalParticipant,
    useRemoteParticipants,
    useRoomContext,
} from '@livekit/react-native';
import { ConnectionState, type DisconnectReason } from 'livekit-client';

import { ENV } from '@/config/env';
import { Colors } from '@/constants/theme';
import { useCallToken } from '@/hooks/useCallToken';
import { useCallAnswered } from '@/hooks/useCallAnswered';
import { useRingback } from '@/hooks/useRingback';
import { PREVIOUS_CALL_CLOSING_MESSAGE, submitCallFeedback, terminateCallWithRetry } from '@/services/calls/client';
import { parsePhoneIdParam } from '@/services/calls/callParams';
import type { CallChannel, CallFeedback } from '@/services/calls/types';
import { callEvents } from '@/services/calls/callEvents';
import {
    CALL_FAILED_MESSAGE,
    classifyDisconnect,
    describeDisconnect,
    disconnectReasonName,
    reportCallFailure,
} from '@/services/calls/callFailure';
import {
    endNativeCall,
    newCallUUID,
    reportNativeCallConnected,
    setNativeMuted,
    startNativeCall,
} from '@/services/calls/callKeep';
import { CallControls } from '@/components/calls/CallControls';
import { CallAvatar, CallErrorView, CallFeedbackView, CallLayout, CallStateView } from '@/components/calls/CallLayout';

/**
 * In-call screen (native). Fetches LiveKit credentials, registers the call with
 * CallKit / ConnectionService (so it gets native call treatment and shows in the
 * iOS call log), joins the LiveKit room, and renders connection state, the
 * participant list, and mute / speaker / hang-up controls. Voice only.
 *
 * Audio session: on iOS CallKit owns the AVAudioSession (registerGlobals uses
 * `autoConfigureAudioSession: false`; callKeep relays activation to WebRTC), so
 * we don't start/stop LiveKit's AudioSession here — only set the preferred
 * output. On Android we manage the session directly.
 *
 * Ending the call: the backend keeps the client's leg alive until it is told
 * otherwise, so every guide-side end must reach `POST /call/terminate` — the
 * hang-up button, the iOS system call UI, a failed join / dropped connection
 * (see [services/calls/callFailure.ts]) and, on Android, the app being
 * backgrounded for too long. Only remote-ended calls skip it.
 *
 * Mute and speaker work from the moment the room is joined (while it still
 * rings), and the number being dialled shows under the name.
 *
 * After the call: once a call that was actually answered ends (or fails and
 * the guide closes the error), the screen stays on the quality survey
 * ([CallFeedbackView] → `POST /call/feedback`) instead of auto-closing; calls
 * nobody picked up have nothing to rate and close as before.
 *
 * The web counterpart [app/call.web.tsx] uses LiveKit's web stack and has no
 * CallKit (the LiveKit RN SDK / react-native-callkeep are native-only).
 */

/**
 * Android only: callkeep is disabled there (see callKeep.ts), so no foreground
 * service holds the microphone and the OS silences capture once the app leaves
 * the foreground — the guide is still "in the room" but the client hears
 * nothing. Rather than leave that dead line open, hang up after this grace
 * period (a quick app switch survives; a real departure ends the call). iOS
 * keeps the call alive through CallKit + the `voip` background mode.
 */
const ANDROID_BACKGROUND_HANGUP_MS = 5000;

export default function CallScreen() {
    const router = useRouter();
    const params = useLocalSearchParams<{
        channel?: string;
        channel_id?: string;
        name?: string;
        phone?: string;
        image?: string;
        phone_id?: string;
    }>();

    const channel = params.channel as CallChannel;
    const channelId = params.channel_id ?? '';
    const calleeName = params.name || 'Contact';
    // The number actually being dialled: the picked PSTN number when the guide
    // chose one, otherwise the client's WhatsApp number. Shown by CallKit as the
    // call handle (lock screen / Recents).
    const phone = params.phone ?? '';
    const image = params.image || '';
    // Route params arrive as strings; absent/garbage stays `undefined` so the
    // token request omits `phone_id` rather than sending NaN or "undefined".
    const phoneId = parsePhoneIdParam(params.phone_id);

    // One CallKit / ConnectionService call id per call attempt (a retry after a
    // failure is a new call, so it gets a fresh id).
    const [callUUID, setCallUUID] = useState(newCallUUID);

    const [audioReady, setAudioReady] = useState(false);
    const [ended, setEnded] = useState(false);
    // Set when the call died on us (failed join, dropped connection): shows an
    // error with Retry instead of the auto-closing "Call ended".
    const [failure, setFailure] = useState<string | null>(null);
    // The room to rate: set instead of the auto-close timer when an answered
    // call ends, and the survey then owns leaving the screen.
    const [feedbackRoom, setFeedbackRoom] = useState<string | null>(null);
    const endedRef = useRef(false);
    const endTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const callStartedRef = useRef(false);
    // Latched by the room content once the other party actually picks up —
    // only those calls get asked how they went.
    const answeredRef = useRef(false);

    const { status, credentials, error, retry } = useCallToken({ channel, channel_id: channelId, phone_id: phoneId });
    const roomName = credentials?.roomName;

    // --- Audio routing. iOS: CallKit owns the session (we only set the preferred
    // output); Android: LiveKit manages the session directly. ---
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                await AudioSession.configureAudio({
                    android: {
                        preferredOutputList: ['earpiece'],
                        audioTypeOptions: AndroidAudioTypePresets.communication,
                    },
                    ios: { defaultOutput: 'earpiece' },
                });
                if (Platform.OS === 'android') {
                    await AudioSession.startAudioSession();
                }
            } catch (e) {
                console.log('Audio session setup error:', e);
            } finally {
                if (!cancelled) setAudioReady(true);
            }
        })();

        return () => {
            cancelled = true;
            if (Platform.OS === 'android') {
                AudioSession.stopAudioSession();
            }
        };
    }, []);

    const leaveScreen = useCallback(() => {
        if (router.canGoBack()) {
            router.back();
        } else {
            router.replace('/(tabs)');
        }
    }, [router]);

    // Idempotent end-of-call: clear the native call, show "ended", then either
    // ask for feedback (answered call) or close. `roomName` is a safe dep: it
    // only changes while no room is mounted (token fetch / retry).
    const endCall = useCallback(() => {
        if (endedRef.current) return;
        endedRef.current = true;
        endNativeCall(callUUID);
        setEnded(true);
        if (answeredRef.current && roomName) {
            setFeedbackRoom(roomName);
            return;
        }
        endTimerRef.current = setTimeout(leaveScreen, 1200);
    }, [leaveScreen, callUUID, roomName]);

    const markAnswered = useCallback(() => {
        answeredRef.current = true;
    }, []);

    // Close from the failure view: a call that had connected before dropping is
    // exactly the kind worth rating ("Call connected but dropped").
    const closeAfterFailure = useCallback(() => {
        if (answeredRef.current && roomName) {
            setFailure(null);
            setFeedbackRoom(roomName);
            return;
        }
        leaveScreen();
    }, [leaveScreen, roomName]);

    const submitFeedback = useCallback(async (feedback: CallFeedback) => {
        if (!feedbackRoom) return;
        // Throws on failure — the form shows the message and offers Skip.
        await submitCallFeedback(feedbackRoom, feedback);
        leaveScreen();
    }, [feedbackRoom, leaveScreen]);

    // Tear down the bridged WhatsApp/PSTN leg on the backend. Guarded so it
    // fires at most once per call no matter how many guide-side end paths run
    // (in-app hang up, the iOS system-UI "End", a failed join, a dropped
    // connection, Android background). If the other side hangs up first the
    // backend already tears its own leg down, so those paths don't call this.
    // Retries on network/5xx failures keep running after the screen is gone,
    // so a short outage still hangs up the client once connectivity returns.
    const terminatedRef = useRef(false);
    const terminateBackendCall = useCallback(() => {
        if (terminatedRef.current) return;
        if (!roomName) return;
        terminatedRef.current = true;
        terminateCallWithRetry(roomName, {}, (e, attempt) =>
            console.log(`Terminate call failed (attempt ${attempt}), retrying:`, (e as Error)?.message),
        ).catch((e) => {
            console.log('Terminate call error:', e);
            reportCallFailure(e, { stage: 'terminate', roomName });
        });
    }, [roomName]);

    // The call died on us: hang up the client's leg (nobody else will) and keep
    // the screen open on an error the guide can act on. Shares `endedRef` with
    // `endCall` so whichever fires first wins and the other becomes a no-op —
    // the CallKit end event that `endNativeCall` triggers lands here too.
    const failCall = useCallback((message: string) => {
        if (endedRef.current) return;
        endedRef.current = true;
        terminateBackendCall();
        endNativeCall(callUUID);
        setFailure(message);
    }, [callUUID, terminateBackendCall]);

    // Start over after a failure: new CallKit id, fresh guards, new token.
    const retryCall = useCallback(() => {
        endedRef.current = false;
        terminatedRef.current = false;
        callStartedRef.current = false;
        answeredRef.current = false;
        setFailure(null);
        setEnded(false);
        setFeedbackRoom(null);
        setCallUUID(newCallUUID());
        retry();
    }, [retry]);

    // Memoised (not inline) on purpose: LiveKitRoom re-runs its connect effect
    // whenever these callbacks change identity. The RN typings declare
    // `onDisconnected` without arguments, but the component is components-react's
    // `useLiveKitRoom`, which forwards the `DisconnectReason` (same as on web).
    const handleDisconnected = useCallback((reason?: DisconnectReason) => {
        const outcome = classifyDisconnect(reason);
        if (outcome === 'failed') {
            const name = disconnectReasonName(reason);
            console.log('LiveKit disconnected unexpectedly:', name);
            reportCallFailure(`LiveKit disconnected: ${name}`, { stage: 'disconnect', reason: name, roomName });
            failCall(describeDisconnect(reason));
            return;
        }
        endCall();
    }, [endCall, failCall, roomName]);

    const handleRoomError = useCallback((e: Error) => {
        console.log('LiveKit room error:', e?.message);
        reportCallFailure(e, { stage: 'room', roomName });
        failCall(CALL_FAILED_MESSAGE);
    }, [failCall, roomName]);

    useEffect(() => () => {
        if (endTimerRef.current) clearTimeout(endTimerRef.current);
    }, []);

    // Android: hang up if the app stays in the background (see the constant).
    const callLive = !!roomName && !ended && !failure;
    useEffect(() => {
        if (Platform.OS !== 'android' || !callLive) return;
        let timer: ReturnType<typeof setTimeout> | null = null;
        const sub = AppState.addEventListener('change', (state) => {
            if (state === 'background') {
                timer ??= setTimeout(() => {
                    timer = null;
                    terminateBackendCall();
                    endCall();
                }, ANDROID_BACKGROUND_HANGUP_MS);
            } else if (state === 'active' && timer) {
                clearTimeout(timer);
                timer = null;
            }
        });
        return () => {
            sub.remove();
            if (timer) clearTimeout(timer);
        };
    }, [callLive, terminateBackendCall, endCall]);

    const livekitReady = status === 'success' && credentials && audioReady && !!ENV.LIVEKIT_URL;

    // Register the outbound call with CallKit / ConnectionService once ready.
    useEffect(() => {
        if (livekitReady && !callStartedRef.current) {
            callStartedRef.current = true;
            startNativeCall({
                uuid: callUUID,
                handle: phone || calleeName,
                name: calleeName,
                isPhoneNumber: !!phone,
            });
        }
    }, [livekitReady, callUUID, phone, calleeName]);

    // "End" from the system call UI → tear down the WhatsApp leg + wind down.
    useEffect(() => {
        return callEvents.on('endCall', ({ callUUID: uuid }) => {
            if (uuid === callUUID) {
                terminateBackendCall();
                endCall();
            }
        });
    }, [callUUID, endCall, terminateBackendCall]);

    return (
        <View style={styles.container}>
            <Stack.Screen options={{ headerShown: false, gestureEnabled: false }} />

            {feedbackRoom ? (
                <CallFeedbackView title={calleeName} image={image} onSubmit={submitFeedback} onSkip={leaveScreen} />
            ) : failure ? (
                <CallErrorView message={failure} onRetry={retryCall} onClose={closeAfterFailure} />
            ) : ended ? (
                <CallStateView title={calleeName} phone={phone} subtitle="Call ended" image={image} />
            ) : !ENV.LIVEKIT_URL ? (
                <CallErrorView
                    message="Calling isn't configured (missing LiveKit URL). Please contact support."
                    onClose={leaveScreen}
                />
            ) : status === 'error' ? (
                <CallErrorView message={error ?? 'Could not start the call.'} onRetry={retry} onClose={leaveScreen} />
            ) : status === 'waiting' ? (
                // The previous call with this client is still closing on the
                // backend; the hook is retrying the token. No call exists yet,
                // so leaving needs no terminate.
                <CallStateView
                    title={calleeName}
                    phone={phone}
                    subtitle={PREVIOUS_CALL_CLOSING_MESSAGE}
                    loading
                    image={image}
                    onCancel={leaveScreen}
                />
            ) : !livekitReady ? (
                <CallStateView title={calleeName} phone={phone} subtitle="Connecting…" loading image={image} />
            ) : (
                <LiveKitRoom
                    serverUrl={ENV.LIVEKIT_URL}
                    token={credentials!.token}
                    connect
                    audio
                    video={false}
                    onConnected={() => reportNativeCallConnected(callUUID)}
                    onDisconnected={handleDisconnected}
                    onError={handleRoomError}
                >
                    <CallRoomContent
                        calleeName={calleeName}
                        phone={phone}
                        image={image}
                        callUUID={callUUID}
                        onEnd={endCall}
                        onTerminate={terminateBackendCall}
                        onAnswered={markAnswered}
                    />
                </LiveKitRoom>
            )}
        </View>
    );
}

interface CallRoomContentProps {
    calleeName: string;
    phone: string;
    image: string;
    callUUID: string;
    /** Called when the call should wind down (hang up / remote left / disconnect). */
    onEnd: () => void;
    /** Tear down the bridged WhatsApp leg (guide-initiated hang up). */
    onTerminate: () => void;
    /** Called once the other party has actually picked up. */
    onAnswered: () => void;
}

/** Lives inside <LiveKitRoom>; reads room state via LiveKit hooks. */
function CallRoomContent({ calleeName, phone, image, callUUID, onEnd, onTerminate, onAnswered }: CallRoomContentProps) {
    const room = useRoomContext();
    const connectionState = useConnectionState();
    const remoteParticipants = useRemoteParticipants();
    const { localParticipant, isMicrophoneEnabled } = useLocalParticipant();
    const [speakerOn, setSpeakerOn] = useState(false);
    const [seconds, setSeconds] = useState(0);

    const hadRemoteRef = useRef(false);

    const connected = connectionState === ConnectionState.Connected;

    // A joined participant is still just dialing/ringing; the real "answered"
    // signal is channel-specific (WhatsApp metadata / PSTN sip.callStatus).
    const answered = useCallAnswered(room);

    // Ring while we're in the room and dialing, until the other side answers.
    useRingback(connected && !answered);

    // Let the screen know the call was picked up (it decides on feedback later).
    useEffect(() => {
        if (answered) onAnswered();
    }, [answered, onAnswered]);

    // Call timer — starts only once the call is actually answered.
    useEffect(() => {
        if (!answered) return;
        const id = setInterval(() => setSeconds((s) => s + 1), 1000);
        return () => clearInterval(id);
    }, [answered]);

    // End the call once everyone else has left (after someone had joined).
    useEffect(() => {
        if (remoteParticipants.length > 0) {
            hadRemoteRef.current = true;
        } else if (hadRemoteRef.current) {
            onEnd();
        }
    }, [remoteParticipants.length, onEnd]);

    // Mute toggled from the system call UI → reflect it in LiveKit.
    useEffect(() => {
        return callEvents.on('setMuted', ({ callUUID: uuid, muted }) => {
            if (uuid === callUUID) {
                localParticipant
                    .setMicrophoneEnabled(!muted)
                    .catch((e) => console.log('Mute (from CallKit) error:', e));
            }
        });
    }, [callUUID, localParticipant]);

    const toggleMute = useCallback(async () => {
        const enable = !isMicrophoneEnabled;
        try {
            await localParticipant.setMicrophoneEnabled(enable);
            setNativeMuted(callUUID, !enable); // keep the CallKit mute button in sync
        } catch (e) {
            console.log('Mute toggle error:', e);
        }
    }, [localParticipant, isMicrophoneEnabled, callUUID]);

    const toggleSpeaker = useCallback(async () => {
        const next = !speakerOn;
        try {
            const deviceId =
                Platform.OS === 'ios'
                    ? next
                        ? 'force_speaker'
                        : 'default'
                    : next
                      ? 'speaker'
                      : 'earpiece';
            await AudioSession.selectAudioOutput(deviceId);
            setSpeakerOn(next);
        } catch (e) {
            console.log('Speaker toggle error:', e);
        }
    }, [speakerOn]);

    const handleHangup = useCallback(async () => {
        // Disconnecting our own LiveKit room does not end the bridged WhatsApp
        // leg — tell the backend to tear it down.
        onTerminate();
        try {
            await room.disconnect();
        } catch (e) {
            console.log('Disconnect error:', e);
        }
        // Route through the same idempotent end path as onDisconnected.
        onEnd();
    }, [room, onEnd, onTerminate]);

    // Both reconnect flavours (full reconnect and signal-only resume) read as
    // "Reconnecting…" — falling through to "Connecting…" mid-call looked like
    // the call had never connected.
    const reconnecting =
        connectionState === ConnectionState.Reconnecting ||
        connectionState === ConnectionState.SignalReconnecting;
    const subtitle =
        reconnecting
            ? 'Reconnecting…'
            : answered
              ? formatDuration(seconds)
              : connected
                ? 'Ringing…'
                : 'Connecting…';

    return (
        <CallLayout
            title={calleeName}
            phone={phone}
            subtitle={subtitle}
            loading={!answered}
            body={<CallAvatar name={calleeName} image={image} />}
            controls={
                <CallControls
                    isMuted={!isMicrophoneEnabled}
                    isSpeakerOn={speakerOn}
                    onToggleMute={toggleMute}
                    onToggleSpeaker={toggleSpeaker}
                    onHangup={handleHangup}
                    // Usable as soon as we're in the room, while it still rings:
                    // the guide wants speaker / mute set before the client picks
                    // up, not mid-conversation. The mic is published at connect
                    // and the audio route is live (the ringback plays through it).
                    disabled={!connected}
                />
            }
        />
    );
}

function formatDuration(totalSeconds: number): string {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: Colors.brand,
    },
});
